import { useTranslation } from 'react-i18next';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type {
  FinopsAlertSeverity,
  FinopsAlertStatusFilter,
  FinopsAlertsFacets,
} from '../../../../shared/types/finops';
import { SeverityLegend } from './AlertSeverityBadge';
import { SEVERITY_VALUES, type AlertsFilterState } from './alerts-format';

/** Radix Select forbids an empty-string value. */
const ALL = '__all__';

export function AlertsFilterBar({
  filters, facets, onChange,
}: {
  filters: AlertsFilterState;
  facets: FinopsAlertsFacets | undefined;
  onChange: (next: AlertsFilterState) => void;
}) {
  const { t } = useTranslation();

  // A filter from a shared link that is not (yet) a facet still shows in the trigger.
  const services = [...(facets?.services ?? [])];
  if (filters.service && !services.includes(filters.service)) services.unshift(filters.service);
  const accounts = [...(facets?.accounts ?? [])];
  if (filters.accountId && !accounts.some((a) => a.accountId === filters.accountId)) {
    accounts.unshift({ accountId: filters.accountId, accountName: filters.accountId });
  }

  const set = (patch: Partial<AlertsFilterState>) => {
    const next: AlertsFilterState = { ...filters, ...patch };
    for (const k of ['severity', 'service', 'accountId'] as const) if (!next[k]) delete next[k];
    onChange(next);
  };

  // Literal keys so audit-i18n can resolve them; Record<FinopsAlertSeverity, …> fails to
  // typecheck if the severity union grows without a label.
  const severityLabels: Record<FinopsAlertSeverity, string> = {
    critical: t('finops.alerts.severity.critical'),
    warning: t('finops.alerts.severity.warning'),
    info: t('finops.alerts.severity.info'),
  };

  const statuses: Array<{ value: FinopsAlertStatusFilter; label: string }> = [
    { value: 'open', label: t('finops.alerts.filters.status_open') },
    { value: 'resolved', label: t('finops.alerts.filters.status_resolved') },
    { value: 'all', label: t('finops.alerts.filters.status_all') },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-md border p-0.5" role="group">
        {statuses.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => set({ status: s.value })}
            aria-pressed={filters.status === s.value}
            className={`h-7 rounded px-3 text-sm transition-colors ${
              filters.status === s.value ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Select
        value={filters.severity ?? ALL}
        onValueChange={(v) => set({ severity: v === ALL ? undefined : (v as FinopsAlertSeverity) })}
      >
        <SelectTrigger className="h-8 text-sm w-40"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('finops.alerts.filters.severity_all')}</SelectItem>
          {SEVERITY_VALUES.map((sev) => (
            <SelectItem key={sev} value={sev}>{severityLabels[sev]}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.service ?? ALL}
        onValueChange={(v) => set({ service: v === ALL ? undefined : v })}
        disabled={services.length === 0}
      >
        <SelectTrigger className="h-8 text-sm w-40"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('finops.alerts.filters.service_all')}</SelectItem>
          {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select
        value={filters.accountId ?? ALL}
        onValueChange={(v) => set({ accountId: v === ALL ? undefined : v })}
        disabled={accounts.length === 0}
      >
        <SelectTrigger className="h-8 text-sm w-56"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('finops.alerts.filters.account_all')}</SelectItem>
          {accounts.map((a) => (
            <SelectItem key={a.accountId} value={a.accountId}>
              {a.accountName === a.accountId ? a.accountId : `${a.accountName} (${a.accountId})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <SeverityLegend />
    </div>
  );
}
